import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Utensils } from "lucide-react";

const SarafaBazaar = () => {
  const navigate = useNavigate();
  const sarafaImage = "/Indore/eating/sarafa0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Sarafa Bazaar",
    "description": "Famous night food market in Indore, Asia's largest street food destination",
    "image": sarafaImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Sarafa Bazaar",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 20:00-02:00",
    "priceRange": "₹₹",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "5000"
    }
  };

  const foodSpecialties = [
    {
      name: "Bhutte ka Kees",
      description: "Grated corn cooked with milk, spices, and garnished with coriander",
      price: "₹40-60",
      popularity: "Must Try",
      vendor: "Multiple stalls"
    },
    {
      name: "Garadu",
      description: "Deep-fried yam pieces served with tangy chutneys",
      price: "₹30-50",
      popularity: "Winter Special",
      vendor: "Famous Garadu Corner"
    },
    {
      name: "Dahi Vada",
      description: "Soft lentil dumplings in yogurt with sweet and tangy chutneys",
      price: "₹40-70",
      popularity: "Year Round",
      vendor: "Hari Om Dahi Vada"
    },
    {
      name: "Kulfi Falooda",
      description: "Traditional ice cream with vermicelli, rose syrup, and nuts",
      price: "₹80-120",
      popularity: "Must Try",
      vendor: "Johny Hot Dog"
    },
    {
      name: "Moong Dal Halwa",
      description: "Rich dessert made with split yellow lentils, ghee, and dry fruits",
      price: "₹60-100",
      popularity: "Seasonal",
      vendor: "Anand Sweets"
    },
    {
      name: "Rabdi Jalebi",
      description: "Creamy rabdi served with crispy, syrupy jalebis",
      price: "₹70-120",
      popularity: "Must Try",
      vendor: "Joshi Dahi Bada House"
    }
  ];

  const visitingTips = [
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Best Time to Visit",
      description: "8:00 PM to 12:00 AM for full experience, avoid very late hours",
      tips: ["Peak time: 9-11 PM", "Less crowded after midnight", "Weekends are busier"]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Safety & Hygiene",
      description: "Choose vendors with good turnover, carry hand sanitizer",
      tips: ["Hot, fresh food is safer", "Bottled water recommended", "Trust popular stalls"]
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Photography Tips",
      description: "Vibrant food colors, bustling atmosphere, ask before clicking vendors",
      tips: ["Good lighting at night", "Food close-ups work well", "Respect vendor privacy"]
    },
    {
      icon: <Car className="w-6 h-6" />,
      title: "Parking & Transport",
      description: "Limited parking, use public transport or drop-off points",
      tips: ["Park at nearby areas", "Auto/taxi easily available", "Walking distance from Rajwada"]
    }
  ];

  const famousStalls = [
    { name: "Johny Hot Dog", specialty: "Kulfi Falooda, Hot Dogs", years: "25+ years" },
    { name: "Hari Om Dahi Vada", specialty: "Dahi Vada, Chaat Items", years: "30+ years" },
    { name: "Garadu Corner", specialty: "Garadu, Corn Items", years: "20+ years" },
    { name: "Joshi Dahi Bada House", specialty: "Dahi Bada, Rabdi", years: "40+ years" }
  ];

  return (
    <>
      <Helmet>
        <title>Sarafa Bazaar Indore - Night Food Paradise | Aaoghumen</title>
        <meta name="description" content="Experience Sarafa Bazaar with Aaoghumen - Asia's largest night food market in Indore. Discover famous street food, vendor recommendations, timings, and food safety tips for the ultimate culinary adventure." />
        <meta name="keywords" content="sarafa bazaar indore, indore night food market, sarafa bazaar food, indore street food, bhutte ka kees sarafa, garadu indore, aaoghumen sarafa bazaar, indore food tour, best food sarafa bazaar" />
        
        <meta property="og:title" content="Sarafa Bazaar Indore - Asia's Largest Night Food Market | Aaoghumen" />
        <meta property="og:description" content="Discover Indore's legendary Sarafa Bazaar - a food lover's paradise with over 100 street food stalls serving authentic local delicacies." />
        <meta property="og:image" content={sarafaImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/sarafa-bazaar" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/sarafa-bazaar" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-yellow-600 via-orange-500 to-red-600 overflow-hidden">
          <img
            src={sarafaImage}
            alt="Sarafa Bazaar night food market Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-60"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-4xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Sarafa Bazaar</span> - Food Paradise
              </h1>
              <p className="text-xl mb-6">
                Experience Asia's largest night food market where jewelry shops transform into a culinary wonderland after sunset, offering Indore's most authentic and delicious street food.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.8 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>8 PM - 2 AM</span>
                </div>
                <div className="flex items-center">
                  <Utensils className="w-5 h-5 mr-2" />
                  <span>100+ Food Stalls</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Sarafa Bazaar</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Sarafa Bazaar is one of India's most unique food destinations, where the traditional jewelry market undergoes a magical transformation every evening. As the sun sets, jewelry shops close their shutters and over 100 food stalls emerge, creating Asia's largest night food market. This incredible metamorphosis has been happening for over three decades, making Sarafa Bazaar synonymous with Indore's food culture.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                What makes Sarafa Bazaar truly special is its authentic street food that represents the heart and soul of Indore's culinary traditions. From the famous Bhutte ka Kees to the seasonal Garadu, every dish tells a story of local flavors and generations of culinary expertise. The market attracts food lovers from across India and beyond, who come specifically to experience this one-of-a-kind food carnival.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, navigate this food paradise like a local, discover the best stalls with decades of history, understand the cultural significance of each dish, and experience the warmth of Indore's food community. Our local guides ensure you taste the most authentic flavors while following food safety practices that make your culinary adventure both delicious and safe.
              </p>
            </div>
          </section>

          {/* Food Specialties */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Must-Try Food Specialties</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {foodSpecialties.map((food, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-orange-600">{food.name}</h3>
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                        {food.popularity}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">{food.description}</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Price:</span>
                        <span className="text-green-600 font-medium">{food.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Best Vendor:</span>
                        <span className="text-blue-600 font-medium">{food.vendor}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Famous Stalls */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Famous Food Stalls</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {famousStalls.map((stall, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{stall.name}</h3>
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {stall.years}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{stall.specialty}</p>
                  <div className="flex items-center text-sm text-orange-600">
                    <Star className="w-4 h-4 mr-2" />
                    <span>Highly Recommended by Locals</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Visiting Tips */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Essential Visiting Tips</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {visitingTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {tip.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{tip.title}</h3>
                  </div>
                  <p className="text-gray-600 mb-4">{tip.description}</p>
                  <ul className="space-y-2">
                    {tip.tips.map((point, i) => (
                      <li key={i} className="text-sm text-gray-500 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Food Safety Guide */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Food Safety Guidelines</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-green-600 mb-4">✓ Safe Practices</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Choose stalls with high customer turnover</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Opt for hot, freshly prepared food</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Carry hand sanitizer and use frequently</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span>Drink bottled water or trusted sources</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-red-600 mb-4">⚠ Precautions</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span>Avoid stalls with poor hygiene conditions</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span>Skip food that's been sitting for too long</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span>Be cautious with dairy products in summer</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></span>
                      <span>Don't overeat - try small portions initially</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach Sarafa Bazaar</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Location & Access</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <MapPin className="w-5 h-5 text-orange-500 mt-1 mr-3" />
                      <span><strong>Address:</strong> Sarafa Bazaar, Old City, Indore</span>
                    </li>
                    <li className="flex items-start">
                      <Car className="w-5 h-5 text-blue-500 mt-1 mr-3" />
                      <span><strong>From Rajwada:</strong> 500m walking distance</span>
                    </li>
                    <li className="flex items-start">
                      <Clock className="w-5 h-5 text-green-500 mt-1 mr-3" />
                      <span><strong>Best Access Time:</strong> After 8:00 PM</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Transport Options</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto Rickshaw:</strong> ₹30-80 from most city areas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Uber/Ola:</strong> Drop at nearby parking areas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>City Bus:</strong> Multiple routes to old city area</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Book Food Tour */}
          <section className="bg-gradient-to-r from-yellow-600 via-orange-500 to-red-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Sarafa Bazaar Like a Local</h2>
              <p className="text-xl mb-6">Join our guided food tours for a safe and authentic culinary adventure in India's food capital</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Sarafa Bazaar, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Book Food Tour
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Get Food Guide
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default SarafaBazaar;